import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Award, Users, Trophy, GraduationCap } from 'lucide-react';

// Counter animation hook
const useCounter = (end, duration = 2000, startOnView = false, isInView = true) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isInView || hasAnimated.current) return;
    
    hasAnimated.current = true;
    const startTime = Date.now();
    const endValue = parseInt(end.toString().replace(/[^0-9]/g, '')) || 0;
    
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * endValue);
      
      setCount(currentCount);
      countRef.current = currentCount;
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, isInView]);

  return count;
};

// Counter component
const AnimatedCounter = ({ value, isInView }) => {
  // Extract number and suffix (like '+', 'x')
  const numericPart = value.toString().replace(/[^0-9]/g, '');
  const prefix = value.toString().match(/^[^0-9]*/)?.[0] || '';
  const suffix = value.toString().match(/[^0-9]*$/)?.[0] || '';
  
  const count = useCounter(numericPart, 2000, true, isInView);
  
  return (
    <span>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

const AboutSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, { once: true, margin: "-100px" });

  // All images in one slideshow - only 3 images
  const allImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/okgybulg_7581.webp",
      title: "Top Performer 2024-25",
      subtitle: "Sylhet Division"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/flp9e61b_95EA0B42-7FC5-473D-B1FA-664C3EaCC3DC5.jpeg",
      title: "Top Performer 2025-26",
      subtitle: "Sylhet Division"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/2qihxhpd_IMG_3371.JPG.jpeg",
      title: "Computer Lab Class",
      subtitle: "Students during computer lab session"
    }
  ];

  // Auto-slide every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % allImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [allImages.length]);

  const achievements = [
    {
      icon: Trophy,
      value: '2x',
      label: 'British Council Top Performer',
      description: 'Sylhet Division - Consecutive Years'
    },
    {
      icon: Users,
      value: '4,000+',
      label: 'Students Served',
      description: 'Through Radiance Education'
    },
    {
      icon: GraduationCap,
      value: '5+',
      label: 'Courses Offered',
      description: 'On-site & Online Programs'
    },
    {
      icon: Award,
      value: '8+',
      label: 'Years Experience',
      description: 'Web Dev & Digital Marketing'
    },
  ];

  return (
    <section id="about" className="py-20 lg:py-32 relative">
      {/* Subtle background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      
      {/* Background accent */}
      <div className="absolute top-1/2 -right-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Light theme decorative elements */}
      <div className="light-decor-blob-1" style={{ top: '-50px', right: '-100px', width: '300px', height: '300px' }} />
      <div className="light-decor-blob-3" style={{ top: '20%', left: '2%', width: '180px', height: '180px' }} />
      <div className="light-decor-circle-1" style={{ top: '30%', left: '3%', width: '60px', height: '60px' }} />
      <div className="light-decor-dot-1" style={{ top: '15%', right: '25%' }} />
      <div className="light-decor-line-1" style={{ top: '5%', left: '10%', width: '50px' }} />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">About Me</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-4">
            Building <span className="text-gradient">Real-World</span> Solutions
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Transforming ideas into production-grade systems with proven impact
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="space-y-5"
          >
            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
              I build real-world software systems used in production. As the 
              <span className="text-foreground font-semibold"> Managing Director of Radiance Education</span>, 
              I developed the institute's core digital platforms—exams, booking, student management, and 
              eligibility checkers—through which <span className="text-primary font-semibold">nearly 4,000 students</span> have 
              completed courses over the past two years.
            </p>
            
            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
              <span className="text-foreground font-semibold">Radiance Education</span>, which I co-founded, achieved the 
              <span className="text-primary font-semibold"> British Council Sylhet Divisional Top Performer Award</span> in 
              its first year (2024–25) and again in the consecutive year (2025–26).
            </p>

            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
              With <span className="text-foreground font-semibold">8+ years of experience</span> in web development, 
              supported by a background in digital marketing and branding, I build systems that are business-ready, 
              not just functional. I'm also the <span className="text-foreground font-semibold">Founder of VirtualSwift</span>, 
              delivering custom web and AI-powered applications globally.
            </p>

            <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
              I also work as a <span className="text-foreground font-semibold">part-time, contract-based web developer</span> for 
              a local government authority.
            </p>

            {/* Highlight box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="glass-strong rounded-2xl p-5 border-l-4 border-primary mt-6"
            >
              <p className="text-sm lg:text-base text-foreground font-medium">
                "I prefer proving capability through working systems and real demos—not claims."
              </p>
            </motion.div>
          </motion.div>

          {/* Right Image Card - Combined Slideshow */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            <div className="relative max-w-md mx-auto">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl blur-2xl scale-105 opacity-60" />
              
              {/* Glass card wrapper */}
              <div className="relative glass-strong rounded-3xl p-2 shadow-xl overflow-hidden">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  {/* Slideshow */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentSlide}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8 }}
                      className="absolute inset-0"
                    >
                      <img
                        src={allImages[currentSlide].src}
                        alt={allImages[currentSlide].title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/30 to-transparent" />
                    </motion.div>
                  </AnimatePresence>
                  
                  {/* Info overlay */}
                  <div className="absolute bottom-4 left-4 right-4 z-20">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentSlide}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.5 }}
                        className="glass-strong rounded-xl p-3"
                      >
                        <p className="text-sm font-semibold text-foreground">{allImages[currentSlide].title}</p>
                        <p className="text-xs text-primary">{allImages[currentSlide].subtitle}</p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Slide indicators */}
                  <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                    {allImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          index === currentSlide 
                            ? 'bg-primary w-6' 
                            : 'bg-muted-foreground/50 hover:bg-muted-foreground'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating badge - 2x Award Winner */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="absolute -top-4 -right-4 glass-strong rounded-2xl p-4 shadow-xl"
              >
                <Trophy className="w-6 h-6 text-primary mb-1" />
                <p className="text-xs font-semibold text-foreground">2x Award Winner</p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stats Grid */}
        <motion.div
          ref={statsRef}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {achievements.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 + index * 0.1, duration: 0.5 }}
              whileHover={{ y: -4 }}
              className="glass-strong rounded-2xl p-6 text-center card-hover group"
            >
              <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-primary flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <stat.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-gradient mb-1">
                <AnimatedCounter value={stat.value} isInView={isStatsInView} />
              </p>
              <p className="text-base font-medium text-foreground mb-1">{stat.label}</p>
              <p className="text-sm text-muted-foreground font-medium">{stat.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export { AboutSection };
